"use client";

import { useState, useEffect, useCallback, useRef } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import Image from "next/image";
import { clsx } from "clsx";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@myapp/ui/tooltip";
import { gangwonRegions, type Region } from "@/data/regions";
import { usePrefersReducedMotion } from "@/hooks/use-accessibility";

interface RegionMapProps {
  onSelect?: (regionId: string) => void;
  className?: string;
}

export function RegionMap({ onSelect, className }: RegionMapProps) {
  const [selectedRegion, setSelectedRegion] = useState<string | null>(null);
  const [hoveredRegion, setHoveredRegion] = useState<string | null>(null);
  const [focusedIndex, setFocusedIndex] = useState<number>(-1);
  const router = useRouter();
  const searchParams = useSearchParams();
  const prefersReducedMotion = usePrefersReducedMotion();
  const mapContainerRef = useRef<HTMLDivElement>(null);

  // URL과 동기화
  useEffect(() => {
    const regionParam = searchParams.get("region");
    if (regionParam && regionParam !== selectedRegion) {
      setSelectedRegion(regionParam);
    }
  }, [searchParams, selectedRegion]);

  const handleRegionSelect = useCallback((regionId: string) => {
    setSelectedRegion(regionId);
    
    // URL 업데이트
    const params = new URLSearchParams(searchParams);
    params.set("region", regionId);
    router.replace(`?${params.toString()}`);
    
    // 상위 컴포넌트에 전달
    onSelect?.(regionId);
  }, [router, searchParams, onSelect]);

  const handleKeyDown = useCallback((event: React.KeyboardEvent, regionId: string) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      handleRegionSelect(regionId);
    }
  }, [handleRegionSelect]);

  // Handle keyboard navigation for the entire map
  const handleMapKeyDown = useCallback((event: React.KeyboardEvent) => {
    const regions = gangwonRegions;
    const currentIndex = focusedIndex >= 0 ? focusedIndex : 0;
    
    switch (event.key) {
      case "ArrowUp":
      case "ArrowLeft":
        event.preventDefault();
        const prevIndex = currentIndex > 0 ? currentIndex - 1 : regions.length - 1;
        setFocusedIndex(prevIndex);
        setHoveredRegion(regions[prevIndex].id);
        // Focus the region element
        const prevElement = mapContainerRef.current?.querySelector(
          `[data-region-id="${regions[prevIndex].id}"]`
        ) as HTMLElement;
        prevElement?.focus();
        break;
        
      case "ArrowDown":
      case "ArrowRight":
        event.preventDefault();
        const nextIndex = currentIndex < regions.length - 1 ? currentIndex + 1 : 0;
        setFocusedIndex(nextIndex);
        setHoveredRegion(regions[nextIndex].id);
        // Focus the region element
        const nextElement = mapContainerRef.current?.querySelector(
          `[data-region-id="${regions[nextIndex].id}"]`
        ) as HTMLElement;
        nextElement?.focus();
        break;
        
      case "Enter":
      case " ":
        event.preventDefault();
        if (focusedIndex >= 0) {
          handleRegionSelect(regions[focusedIndex].id);
        }
        break;
        
      case "Escape":
        event.preventDefault();
        setFocusedIndex(-1);
        setHoveredRegion(null);
        break;
    }
  }, [focusedIndex, handleRegionSelect]);

  const getRegionClassName = (regionId: string) => {
    return clsx(
      prefersReducedMotion ? "cursor-pointer outline-none" : "transition-all duration-200 cursor-pointer outline-none",
      "fill-primary/10 stroke-transparent stroke-2",
      "hover:fill-primary/20 hover:stroke-primary/50",
      "focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
      {
        "fill-primary/30 stroke-primary": selectedRegion === regionId,
        "fill-primary/25": hoveredRegion === regionId && selectedRegion !== regionId
      }
    );
  };

  const selectedRegionData = gangwonRegions.find(r => r.id === selectedRegion);

  return (
    <div className={clsx("relative w-full aspect-[4/3] bg-background rounded-lg overflow-hidden", className)}>
      {/* 배경 지도 이미지 */}
      <div className="absolute inset-0 bg-gradient-to-br from-green-50 to-green-100 dark:from-green-950 dark:to-green-900">
        <div className="absolute inset-0 opacity-30" style={{backgroundImage: "url('data:image/svg+xml,%3Csvg width=%2220%22 height=%2220%22 xmlns=%22http://www.w3.org/2000/svg%22%3E%3Cdefs%3E%3Cpattern id=%22grid%22 width=%2220%22 height=%2220%22 patternUnits=%22userSpaceOnUse%22%3E%3Cpath d=%22M 20 0 L 0 0 0 20%22 fill=%22none%22 stroke=%22%23000000%22 stroke-opacity=%220.05%22 stroke-width=%221%22/%3E%3C/pattern%3E%3C/defs%3E%3Crect width=%22100%25%22 height=%22100%25%22 fill=%22url(%23grid)%22/%3E%3C/svg%3E')"}} />
      </div>
      
      {/* 상호작용 SVG 오버레이 */}
      <TooltipProvider>
        <svg 
          className="absolute inset-0 w-full h-full" 
          viewBox="0 0 350 300"
          role="img" 
          aria-label="강원도 시군 상호작용 지도"
        >
          {gangwonRegions.map((region: Region) => (
            <Tooltip key={region.id}>
              <TooltipTrigger asChild>
                <path
                  d={region.path}
                  className={getRegionClassName(region.id)}
                  tabIndex={0}
                  data-region-id={region.id}
                  onClick={() => handleRegionSelect(region.id)}
                  onKeyDown={(e) => handleKeyDown(e, region.id)}
                  onMouseEnter={() => setHoveredRegion(region.id)}
                  onMouseLeave={() => setHoveredRegion(null)}
                  aria-label={`${region.title} 선택`}
                />
              </TooltipTrigger>
              <TooltipContent side="top">
                <p className="font-medium">{region.title}</p>
                {selectedRegion === region.id && (
                  <p className="text-xs text-muted-foreground">선택됨</p>
                )}
              </TooltipContent>
            </Tooltip>
          ))}
        </svg>
      </TooltipProvider>

      {/* 선택된 지역 정보 */}
      {selectedRegionData && (
        <div className="absolute bottom-4 left-4 bg-card/90 backdrop-blur-sm border rounded-lg px-3 py-2 shadow-lg">
          <p className="text-sm font-medium text-card-foreground">
            선택된 지역: <span className="text-primary">{selectedRegionData.title}</span>
          </p>
        </div>
      )}

      {/* 범례 */}
      <div className="absolute top-4 right-4 bg-card/90 backdrop-blur-sm border rounded-lg px-3 py-2 shadow-lg">
        <div className="flex flex-col gap-1 text-xs">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-sm bg-primary/10 border border-border" />
            <span>일반</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-sm bg-primary/20 border border-primary/50" />
            <span>호버</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-sm bg-primary/30 border border-primary" />
            <span>선택</span>
          </div>
        </div>
      </div>
    </div>
  );
}